import { createSlice, isAsyncThunkAction, isPending, createAsyncThunk, createDraftSafeSelector, combineReducers, configureStore } from '@reduxjs/toolkit';
export { combineReducers, compose, createAction, createAsyncThunk, createDraftSafeSelector, createEntityAdapter, createReducer, createSelector } from '@reduxjs/toolkit';
import { useSelector, useDispatch } from 'react-redux';
export { Provider, useDispatch, useSelector } from 'react-redux';
import { useMemo } from 'react';
import { bindActionCreators } from 'redux';

var DEFAULT_LOADING_NAME = '$$thunkLoading';

var createThunkLoadingPlugin = function createThunkLoadingPlugin(options) {
  var _reducer;

  var _ref = options || {},
      _ref$name = _ref.name,
      name = _ref$name === void 0 ? DEFAULT_LOADING_NAME : _ref$name;

  var _createSlice = createSlice({
    name: name,
    initialState: {},
    reducers: {
      setLoading: function setLoading(state, _ref2) {
        var payload = _ref2.payload;
        state[payload.type] = payload.status;
      }
    }
  }),
      actions = _createSlice.actions,
      reducer = _createSlice.reducer;

  return {
    reducer: (_reducer = {}, _reducer[name] = reducer, _reducer),
    middleware: function middleware(_ref3) {
      var dispatch = _ref3.dispatch;
      return function (next) {
        return function (action) {
          if (isAsyncThunkAction(action)) {
            var type = action.type;
            dispatch({
              type: actions.setLoading.type,
              payload: {
                type: type.slice(0, type.lastIndexOf('/')),
                status: isPending(action)
              }
            });
          }

          return next(action);
        };
      };
    }
  };
};
var thunkLoadingPlugin = /*#__PURE__*/createThunkLoadingPlugin();

var session = {
  storageKey: '$$reduxStoreState',
  set: function set(v) {
    sessionStorage.setItem(this.storageKey, JSON.stringify(v));
  },
  get: function get() {
    return JSON.parse(sessionStorage.getItem(this.storageKey) || '{}');
  },
  remove: function remove() {
    sessionStorage.removeItem(this.storageKey);
  }
};
function createStoragePlugin(config) {
  var _ref = config || {},
      _ref$enable = _ref.enable,
      enable = _ref$enable === void 0 ? true : _ref$enable,
      _ref$storage = _ref.storage,
      storage = _ref$storage === void 0 ? session : _ref$storage,
      _ref$include = _ref.include,
      include = _ref$include === void 0 ? [] : _ref$include,
      _ref$exclude = _ref.exclude,
      exclude = _ref$exclude === void 0 ? [] : _ref$exclude;

  if (!enable) {
    storage.remove();
    return {};
  }

  return {
    preloadState: storage.get(),
    enhancers: function enhancers(next) {
      return function () {
        var store = next.apply(void 0, arguments);
        store.subscribe(function () {
          var state = store.getState();

          if (include && include.length) {
            var cache = {};
            include.forEach(function (key) {
              return cache[key] = state[key];
            });
            storage.set(cache);
            return;
          }

          if (exclude && exclude.length) {
            var _cache = {};
            Object.keys(state).forEach(function (key) {
              if (exclude.indexOf(key) === -1) {
                _cache[key] = state[key];
              }
            });
            storage.set(_cache);
          }
        });
        return store;
      };
    }
  };
}
/** 将store数据缓存到本地的插件 */

var storagePlugin = /*#__PURE__*/createStoragePlugin();

var createUseThunkLoading = function createUseThunkLoading(name) {
  if (name === void 0) {
    name = DEFAULT_LOADING_NAME;
  }

  var useLoading = function useLoading(action) {
    if (!isAsyncThunkAction(action)) {
      throw new TypeError('useLoading parameter "action" must be ActionsFromAsyncThunk');
    }

    return useSelector(function (state) {
      var loadingMap = state[name] && state[name];
      return loadingMap ? !!loadingMap[action.typePrefix] : false;
    });
  };

  return useLoading;
};
var useThunkLoading = /*#__PURE__*/createUseThunkLoading();

var defaultActions = {};
function useActions(actions1, actions2, actions3) {
  if (actions1 === void 0) {
    actions1 = defaultActions;
  }

  if (actions2 === void 0) {
    actions2 = defaultActions;
  }

  if (actions3 === void 0) {
    actions3 = defaultActions;
  }

  var dispatch = useDispatch();
  return useMemo(function () {
    return bindActionCreators(Object.assign({}, actions1, actions2), dispatch);
  }, [actions1, actions2, actions3, dispatch]);
}

var getCreateThunks = function getCreateThunks(name) {
  return function (thunks) {
    var thunksMap = {};
    Object.entries(thunks).forEach(function (_ref) {
      var key = _ref[0],
          payloadCreator = _ref[1];
      var type = name + "/" + key;
      var thunkCreator;

      if (typeof payloadCreator === 'function') {
        thunkCreator = createAsyncThunk(type, payloadCreator);
      } else {
        if (Array.isArray(payloadCreator)) {
          var creator = payloadCreator[0],
              options = payloadCreator[1];
          thunkCreator = createAsyncThunk(type, creator, options);
        } else {
          var thunk = payloadCreator.thunk,
              _options = payloadCreator.options;
          var asyncThunk = createAsyncThunk(type, thunk, _options);
          thunkCreator = asyncThunk;
        }
      }

      thunksMap[key] = thunkCreator;
    });
    return thunksMap;
  };
};
var createThunks = function createThunks(name, thunks) {
  return getCreateThunks(name)(thunks);
};

var createSelectors = function createSelectors(name, initialState) {
  var rootSelector = function rootSelector(state) {
    return state[name];
  };

  var getSelector = function getSelector(currState, preSelector) {
    var selectorMap = {}; // eslint-disable-next-line no-restricted-syntax

    var _loop = function _loop(key) {
      if (Object.prototype.hasOwnProperty.call(currState, key)) {
        var value = currState[key];
        var currSelector = createDraftSafeSelector(preSelector, function (s) {
          return s[key];
        });

        if (typeof value === 'object' && value !== null) {
          if (process.env.NODE_ENV === 'development') {
            if (key in currSelector) {
              throw new Error("\u521B\u5EFAselectors\u65F6\uFF0Cstate\u4E2D\u7684\u5C5E\u6027\u540D\u4E0D\u80FD\u4E3A" + key);
            }
          }

          selectorMap[key] = Object.assign(currSelector, getSelector(value, currSelector));
        } else {
          selectorMap[key] = currSelector;
        }
      }
    };

    for (var key in currState) {
      _loop(key);
    }

    return selectorMap;
  };

  return getSelector(initialState, rootSelector);
};

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

var _excluded = ["thunks", "thunksBuilder", "extraReducers"];
var createModel = function createModel(options) {
  var thunkCreators = options.thunks,
      thunksBuilder = options.thunksBuilder,
      _extraReducers = options.extraReducers,
      sliceOptions = _objectWithoutPropertiesLoose(options, _excluded);

  var createThunks = getCreateThunks(options.name);
  var thunks = thunkCreators ? createThunks(thunkCreators) : {};
  var slice = createSlice(_extends({}, sliceOptions, {
    extraReducers: function extraReducers(builder) {
      typeof _extraReducers === 'function' && _extraReducers(builder);
      typeof thunksBuilder === 'function' && thunks && thunksBuilder(thunks, builder);
    }
  }));
  var selectors = createSelectors(slice.name, options.initialState);
  return _extends({}, slice, {
    thunks: thunks,
    selectors: selectors
  });
};
var getCreateModelWidthApp = function getCreateModelWidthApp(app) {
  return function (options) {
    var model = createModel(options);
    app.injectSlice(model);
    return model;
  };
};

var plugins = [];

var add = function add(p) {
  plugins.push(p);
};

function get(key) {
  return plugins.map(function (p) {
    return p && p[key];
  }).filter(Boolean);
}

var plugin = {
  add: add,
  get: get
};

var configStore = function configStore(options) {
  var _ref = options || {},
      reducer = _ref.reducer,
      _middleware = _ref.middleware,
      devTools = _ref.devTools,
      _ref$preloadedState = _ref.preloadedState,
      preloadedState = _ref$preloadedState === void 0 ? {} : _ref$preloadedState,
      _ref$enhancers = _ref.enhancers,
      enhancers = _ref$enhancers === void 0 ? [] : _ref$enhancers;

  var pluginReducer = plugin.get('reducer');
  var pluginMiddleware = plugin.get('middleware');
  var pluginPreloadState = plugin.get('preloadState');
  var pluginEnhancers = plugin.get('enhancers');
  var reducers = Object.assign.apply(Object, [{}].concat(pluginReducer, [reducer || {}]));
  var finalReducer = Object.keys(reducers).length > 0 ? combineReducers(reducers) : function (s) {
    return s;
  };
  var store = configureStore({
    reducer: finalReducer,
    middleware: function middleware(getDefaultMiddleware) {
      if (typeof _middleware === 'function') {
        return _middleware(function (options) {
          return [].concat(getDefaultMiddleware(options), pluginMiddleware);
        });
      } else {
        return [].concat(getDefaultMiddleware(), pluginMiddleware, Array.isArray(_middleware) ? _middleware : []);
      }
    },
    devTools: devTools,
    preloadedState: Object.assign.apply(Object, [{}].concat(pluginPreloadState, [preloadedState])),
    enhancers: typeof enhancers === 'function' ? function (defaultEnhancers) {
      return enhancers([].concat(defaultEnhancers, pluginEnhancers));
    } : [].concat(pluginEnhancers, enhancers)
  });
  return _extends({}, store, {
    __reducers: reducers
  });
};
var configReduxApp = function configReduxApp(options) {
  var store;
  var asyncReducers = {};
  return {
    addPlugin: function addPlugin(p) {
      plugin.add(p);
      return this;
    },
    complete: function complete(cb) {
      store = configStore(options);
      typeof cb === 'function' && cb();
      return this;
    },
    getStore: function getStore() {
      return store || (store = configStore(options));
    },
    createSlice: function createSlice$1(options) {
      var slice = createSlice(options);

      this.injectSlice(slice);
      return slice;
    },
    createModel: function createModel$1(options) {
      var model = createModel(options);

      this.injectModel(model);
      return model;
    },
    addReducer: function addReducer(name, reducer) {
      if (process.env.NODE_ENV === 'development') {
        if (!store) {
          throw new Error('this method cannot be used before complete');
        }
      }

      var initialReducers = store.__reducers;

      if (asyncReducers[name] || initialReducers[name]) {
        // 防止重复注入
        return this;
      }

      asyncReducers[name] = reducer;
      var allRreducer = combineReducers(_extends({}, initialReducers, asyncReducers));
      store.replaceReducer(allRreducer);
      return this;
    },
    removeReducer: function removeReducer(name) {
      var initialReducers = store.__reducers;

      var replace = function replace() {
        var allRreducer = combineReducers(_extends({}, initialReducers, asyncReducers));
        store.replaceReducer(allRreducer);
      };

      if (asyncReducers[name]) {
        delete asyncReducers[name];
        replace();
        return this;
      }

      if (initialReducers[name]) {
        delete initialReducers[name];
        replace();
        return this;
      }

      console.warn(name + "\u5BF9\u5E94\u7684reducer\u4E0D\u5B58\u5728");
      return this;
    },
    injectSlice: function injectSlice(slice) {
      return this.addReducer(slice.name, slice.reducer);
    },
    injectModel: function injectModel(model) {
      return this.addReducer(model.name, model.reducer);
    }
  };
};

export { DEFAULT_LOADING_NAME, configReduxApp, configStore, createModel, createSelectors, createStoragePlugin, createThunkLoadingPlugin, createThunks, getCreateModelWidthApp, getCreateThunks, storagePlugin, thunkLoadingPlugin, useActions, useThunkLoading };
//# sourceMappingURL=cosmos-redux.esm.js.map
